import {
	type IBaseBusiness,
	type IBaseContacts,
	type IBaseCreateEditDate,
	type IBaseEntityName,
	type IBaseFullname,
	type IBaseId,
} from './base.interface';

/** Mirrors `IUser` in `server/src/core/users/entities/user.interface.ts`. */
export interface IUser
	extends
		IBaseId,
		IBaseFullname,
		IBaseContacts,
		IBaseCreateEditDate,
		IBaseEntityName<'user'>,
		IBaseBusiness {
	username: string;
	roles?: string[];
	type: UserType;
	/** Unset on records created before this field existed. */
	status?: UserStatus;
	/** Oldest first. Unset on records created before this field existed. */
	statusHistory?: IUserStatusHistoryEntry[];
	password: string;
	verificationCode: string;
	settings?: IUserSettings;
}

/** One step in an account's life, shown as a timeline. `status` is the status in effect at that step. */
export interface IUserStatusHistoryEntry {
	status: UserStatus;
	description: string;
	createdAtDate: string;
	/** Who made the change. Missing when the person registered themselves, and on older records. */
	creatorUserId?: string;
}

export interface IOwner
	extends
		IBaseId,
		IBaseFullname,
		IBaseContacts,
		Pick<
			IUser,
			| 'entityName'
			| 'password'
			| 'createdAtDate'
			| 'verificationCode'
			| 'type'
			| 'settings'
		> {}

export interface IUserSettings {
	acceptTermsCondition: boolean;
	acceptSmsNotification?: boolean;
	acceptEmailNotification: boolean;
	preferredTheme?: UserPreferThemeType;
}

export interface ICurrentUser extends Omit<IUser, 'password'> {
	businessOwnerId: string;
	permissions: string[];
}

export const UserTypeEnum = {
	USER: 'user', // self scoped
	OWNER: 'owner', // business scoped, the business owner
} as const;

/** An account's lifecycle. Owners go pending-verification -> active; staff go invited -> active. */
export const UserStatusEnum = {
	PENDING_VERIFICATION: 'pending-verification',
	INVITED: 'invited',
	ACTIVE: 'active',
	SUSPENDED: 'suspended',
	DEACTIVATED: 'deactivated',
} as const;

export const UserPreferThemeEnum = {
	DARK: 'dark',
	LIGHT: 'light',
} as const;

export type UserType = (typeof UserTypeEnum)[keyof typeof UserTypeEnum];
export type UserStatus = (typeof UserStatusEnum)[keyof typeof UserStatusEnum];
export type UserPreferThemeType =
	(typeof UserPreferThemeEnum)[keyof typeof UserPreferThemeEnum];

/** One team member as the user page shows them: everything on the record except the hashes, which the API never sends. */
export type IUserDetail = Omit<IUser, 'password' | 'verificationCode'>;

/** A user as the team list shows them: the safe subset of `IUser` the API returns for each member. Staff users have a username, not an email; only the owner has an email. */
export type IUserListItem = Partial<
	Pick<IUser, 'firstName' | 'surName' | 'username' | 'email'>
> &
	Pick<IUser, 'id' | 'status' | 'roles'>;
